import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Phone, Mail, MapPin, ShieldCheck, LogOut, Trash2 } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';

export default function CustomerAccount() {
  const navigate = useNavigate();
  const { customer, logout } = useCustomerAuth();

  const handleLogout = () => {
    logout();
    navigate('/customer-login');
  };

  return (
    <main className="min-h-screen pt-24 pb-16 bg-[#f8fafc]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="shadow-lg border-slate-200 bg-white overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 sm:p-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white text-2xl font-bold font-['Oswald'] border-2 border-white/30">
                    {customer?.name ? customer.name.charAt(0).toUpperCase() : 'C'}
                  </div>
                  <div>
                    <CardTitle className="text-2xl sm:text-3xl font-bold font-['Oswald'] text-white">
                      {customer?.name || 'Customer Profile'}
                    </CardTitle>
                    <CardDescription className="text-blue-100 text-sm">
                      {customer?.mobile ? `+91 ${customer.mobile}` : 'VHFMS Customer'}
                    </CardDescription>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="bg-white/10 hover:bg-white/20 text-white border-white/30 gap-2 text-sm"
                  >
                    <LogOut size={16} />
                    <span>Sign Out</span>
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-6 sm:p-8 space-y-6">
              <h2 className="text-lg font-bold text-slate-900 font-['Oswald'] pb-2 border-b border-slate-100">
                Account Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Full Name */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-3">
                  <User className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Full Name</p>
                    <p className="text-sm font-semibold text-slate-900">{customer?.name || 'N/A'}</p>
                  </div>
                </div>

                {/* Mobile Number */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-3">
                  <Phone className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Mobile Number</p>
                    <p className="text-sm font-semibold text-slate-900">{customer?.mobile || 'N/A'}</p>
                  </div>
                </div>

                {/* Email (if available) */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Email Address</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {customer?.email ? customer.email : <span className="text-slate-400 font-normal">Not provided (manageable via mobile app)</span>}
                    </p>
                  </div>
                </div>

                {/* Gender (if available) */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-3">
                  <User className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Gender</p>
                    <p className="text-sm font-semibold text-slate-900 capitalize">
                      {customer?.gender ? customer.gender : <span className="text-slate-400 font-normal">Not specified</span>}
                    </p>
                  </div>
                </div>

                {/* Address (if available) */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-3 md:col-span-2">
                  <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Address</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {customer?.address ? customer.address : <span className="text-slate-400 font-normal">No default address saved</span>}
                    </p>
                  </div>
                </div>

                {/* Account Status */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-3 md:col-span-2">
                  <ShieldCheck className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Account Status</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                      {customer?.account_status || 'Active'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="pt-6 border-t border-slate-100">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-red-50 rounded-xl border border-red-100">
                  <div>
                    <h3 className="text-sm font-semibold text-red-900">Delete Customer Account</h3>
                    <p className="text-xs text-red-700">Permanently clear and anonymize your account details and booking history.</p>
                  </div>
                  <Link to="/delete-account">
                    <Button
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700 text-white text-xs gap-1.5 flex-shrink-0"
                    >
                      <Trash2 size={14} />
                      <span>Delete Account</span>
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </main>
  );
}
